import { useCallback, useEffect, useRef, useState } from "react";
import type { PointerEvent } from "react";
import { Box, Tooltip } from "@mui/material";
import { useIntl } from "react-intl";

import type { IChartZoomState } from "../../../../../../../components/common/chart-plugins";
import type { IChartZoomOverview } from "./chart-zoom";
import { getChartOverviewViewport } from "./chart-zoom-overview-geometry";

interface IProps {
  overview: IChartZoomOverview | null;
  zoomState: IChartZoomState | null;
  onPan: (initialState: IChartZoomState, deltaX: number, deltaY: number) => void;
  top?: number;
  right?: number;
}

interface IDrag {
  pointerId: number;
  clientX: number;
  clientY: number;
  width: number;
  height: number;
  zoomState: IChartZoomState;
}

interface IOverviewDrag {
  pointerId: number;
  clientX: number;
  clientY: number;
  left: number;
  top: number;
}

const clampPosition = (value: number): number => Math.min(1, Math.max(0, value));

export const ChartZoomOverview = ({ overview, zoomState, onPan, top = 12, right = 12 }: IProps) => {
  const { formatMessage } = useIntl();
  const overviewElementRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<IDrag | null>(null);
  const overviewDragRef = useRef<IOverviewDrag | null>(null);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [isViewportHovered, setIsViewportHovered] = useState(false);
  const frameRef = useRef<number | null>(null);
  const isVisible = Boolean(overview && zoomState);

  const cancelDrag = useCallback(() => {
    dragRef.current = null;
    overviewDragRef.current = null;
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
  }, []);

  useEffect(() => cancelDrag, [cancelDrag, overview, isVisible]);

  if (!overview || !zoomState) return null;

  const viewport = getChartOverviewViewport(zoomState, overview.domain);
  const label = formatMessage({ id: "pages.scheme.analytics.zoomOverview", defaultMessage: "Мини-график" });
  const instruction = formatMessage({
    id: "pages.scheme.analytics.zoomOverviewPan",
    defaultMessage: "Перемещайте рамку, удерживая левую кнопку мыши",
  });
  const moveInstruction = formatMessage({
    id: "pages.scheme.analytics.zoomOverviewMove",
    defaultMessage: "Перемещайте мини-график, удерживая левую кнопку мыши",
  });

  const moveOverview = (left: number, nextTop: number) => {
    const element = overviewElementRef.current;
    const parent = element?.parentElement;
    if (!element || !parent) return;
    const width = parent.clientWidth - element.offsetWidth;
    const height = parent.clientHeight - element.offsetHeight;
    setPosition({
      x: width > 0 ? clampPosition(left / width) : 0,
      y: height > 0 ? clampPosition(nextTop / height) : 0,
    });
  };

  const startOverviewDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || !event.isPrimary) return;
    const element = event.currentTarget;
    const parent = element.parentElement;
    if (!parent) return;
    event.preventDefault();
    event.stopPropagation();
    cancelDrag();
    const rect = element.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();
    overviewDragRef.current = {
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      left: rect.left - parentRect.left - parent.clientLeft,
      top: rect.top - parentRect.top - parent.clientTop,
    };
    element.setPointerCapture(event.pointerId);
  };

  const moveOverviewDrag = (event: PointerEvent<HTMLDivElement>, complete = false) => {
    const drag = overviewDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.stopPropagation();
    moveOverview(drag.left + event.clientX - drag.clientX, drag.top + event.clientY - drag.clientY);
    if (complete) {
      cancelDrag();
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const startDrag = (event: PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0 || !event.isPrimary) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect?.width || !rect.height) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      width: rect.width,
      height: rect.height,
      zoomState,
    };
  };

  const moveDrag = (event: PointerEvent<HTMLButtonElement>, complete = false) => {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.pointerId) return;
    event.stopPropagation();
    const deltaX = (event.clientX - drag.clientX) / drag.width;
    const deltaY = (event.clientY - drag.clientY) / drag.height;
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    if (complete) {
      onPan(drag.zoomState, deltaX, deltaY);
      cancelDrag();
      event.currentTarget.releasePointerCapture(event.pointerId);
    } else {
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        onPan(drag.zoomState, deltaX, deltaY);
      });
    }
  };

  return (
    <Tooltip title={isViewportHovered ? instruction : moveInstruction} arrow disableInteractive>
      <Box
        ref={overviewElementRef}
        role="group"
        aria-label={label}
        tabIndex={0}
        data-testid="chart-zoom-overview"
        // Percentages and the matching translation keep the panel inside its parent on resize.
        style={
          position
            ? {
                left: `${position.x * 100}%`,
                top: `${position.y * 100}%`,
                right: "auto",
                transform: `translate(${-position.x * 100}%, ${-position.y * 100}%)`,
              }
            : undefined
        }
        sx={{
          position: "absolute",
          top,
          right,
          display: "flex",
          flexDirection: "column",
          width: "28%",
          maxWidth: Math.min(320, (160 * overview.width) / overview.height) + 10,
          maxHeight: `calc(100% - ${top}px)`,
          boxSizing: "border-box",
          zIndex: 11,
          bgcolor: "background.paper",
          border: "2px solid",
          borderColor: "text.secondary",
          boxShadow: 3,
          p: 0.5,
          userSelect: "none",
          cursor: "grab",
          touchAction: "none",
          "&:active": { cursor: "grabbing" },
          "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main" },
        }}
        onPointerDown={startOverviewDrag}
        onPointerMove={event => moveOverviewDrag(event)}
        onPointerUp={event => moveOverviewDrag(event, true)}
        onPointerCancel={cancelDrag}
        onLostPointerCapture={cancelDrag}
        onMouseDown={event => event.stopPropagation()}
        onClick={event => event.stopPropagation()}
        onDoubleClick={event => event.stopPropagation()}
      >
        <Box
          ref={containerRef}
          sx={{
            position: "relative",
            width: "100%",
            minHeight: 0,
            aspectRatio: `${overview.width} / ${overview.height}`,
          }}
        >
          <Box
            component="img"
            src={overview.imageUrl}
            alt={label}
            draggable={false}
            sx={{ display: "block", width: "100%", height: "100%", pointerEvents: "none" }}
          />
          <Box
            component="button"
            type="button"
            aria-label={instruction}
            data-testid="chart-zoom-overview-viewport"
            onPointerDown={startDrag}
            onPointerMove={event => moveDrag(event)}
            onPointerUp={event => moveDrag(event, true)}
            onPointerCancel={cancelDrag}
            onLostPointerCapture={cancelDrag}
            onMouseEnter={() => setIsViewportHovered(true)}
            onMouseLeave={() => setIsViewportHovered(false)}
            sx={{
              position: "absolute",
              left: `${viewport.left * 100}%`,
              top: `${viewport.top * 100}%`,
              width: `${(viewport.right - viewport.left) * 100}%`,
              height: `${(viewport.bottom - viewport.top) * 100}%`,
              boxSizing: "border-box",
              border: "2px solid #ad42b1",
              bgcolor: "rgba(173, 66, 177, 0.08)",
              p: 0,
              cursor: "grab",
              "&:active": { cursor: "grabbing" },
              touchAction: "none",
              "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
            }}
          />
        </Box>
      </Box>
    </Tooltip>
  );
};
